import { useEffect, useState } from "react";
import { Logo } from "@/components/comiq/Logo";
import { COLORS } from "@/comiq/tokens";
import { ArrowDown, ArrowUpRight, Building2, ChevronDown } from "lucide-react";

const WRAP = "mx-auto w-full max-w-[1180px] px-6";

const BLOCK_MSG = {
  submitted: "Jawaban Anda sudah terekam. Setiap undangan hanya dapat dikirim satu kali.",
  expired: "Masa berlaku tautan telah berakhir. Minta tautan baru kepada koordinator riset Anda.",
  revoked: "Undangan ini telah dicabut. Hubungi koordinator riset Anda bila ini tidak seharusnya terjadi.",
  withdrawn: "Partisipasi Anda telah ditarik.",
  wave_closed: "Survei ini sudah ditutup dan tidak lagi menerima jawaban.",
  instrument_not_ready: "Instrumen studi belum siap. Silakan coba lagi nanti atau hubungi tim riset.",
};

function Headline({ title, emphasis }) {
  if (!title) return null;
  if (emphasis && title.includes(emphasis)) {
    const idx = title.indexOf(emphasis);
    return (
      <>
        {title.slice(0, idx)}
        <em style={{ color: COLORS.orange }}>{emphasis}</em>
        {title.slice(idx + emphasis.length)}
      </>
    );
  }
  return title;
}

const PILLAR_ICONS = [
  <svg key="p1" viewBox="0 0 150 80" aria-hidden="true" className="h-14 w-auto"><path d="M0 55C40 55 34 10 70 10S101 66 140 27" fill="none" stroke={COLORS.orangeInk} strokeWidth="1.4"/><path d="M0 62C45 63 38 24 72 24S108 77 140 39" fill="none" stroke={COLORS.lineStrong} strokeWidth="1.2" opacity=".6"/><circle cx="70" cy="10" r="3.5" fill={COLORS.orange}/></svg>,
  <svg key="p2" viewBox="0 0 150 80" aria-hidden="true" className="h-14 w-auto"><path d="M10 18H62L108 50H143M10 58L62 18V64H109V9" fill="none" stroke={COLORS.orangeInk} strokeWidth="1.4"/><circle cx="62" cy="18" r="3.5" fill={COLORS.orange}/><circle cx="109" cy="50" r="3.5" fill={COLORS.orange}/></svg>,
  <svg key="p3" viewBox="0 0 150 80" aria-hidden="true" className="h-14 w-auto"><path d="M0 53C40 54 68 45 140 10M0 62C50 62 94 39 140 38M0 70C53 68 110 60 140 50" fill="none" stroke={COLORS.orangeInk} strokeWidth="1.4"/><circle cx="110" cy="24" r="3.5" fill={COLORS.orange}/></svg>,
];

function HeroVisual() {
  return (
    <svg viewBox="0 0 520 420" aria-hidden="true" className="h-auto w-full" data-testid="landing-visual">
      {[...Array(13)].map((_, i) => (
        <line key={i} x1={20 + i * 40} y1="20" x2={20 + i * 40} y2="400" stroke={COLORS.lineStrong} strokeWidth="0.6" opacity="0.45" />
      ))}
      <g stroke="#9b694c" strokeWidth="1" fill="none" opacity="0.5">
        <path d="M60 300 L160 200 L260 250 L360 150 L470 210" />
        <path d="M60 300 L160 340 L260 250 L360 330 L470 210" opacity="0.5" />
      </g>
      {[[60, 300], [160, 200], [260, 250], [360, 150], [470, 210], [160, 340], [360, 330]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.4" fill="#76503a" opacity="0.7" />
      ))}
      <path d="M20 382 C140 360 200 300 280 260 S420 200 500 92" fill="none" stroke={COLORS.orange} strokeWidth="2.2" opacity="0.85" />
      <circle cx="500" cy="92" r="11" fill={COLORS.orange} opacity="0.12" />
      <circle cx="500" cy="92" r="4.5" fill={COLORS.orange} />
    </svg>
  );
}

export default function SurveyLanding({ data, onStart, busy }) {
  const landing = data?.landing || {};
  const [openFaq, setOpenFaq] = useState(0);
  const email = landing.contact_email || "survey@comiq.id";
  const facts = [
    { label: "Estimasi waktu", value: `± ${landing.estimated_minutes ?? 11} menit`, tid: "fact-time" },
    { label: "Partisipasi", value: landing.participation_label, tid: "fact-participation" },
    { label: "Akses survei", value: landing.access_label, tid: "fact-access" },
    { label: "Pelaporan", value: landing.reporting_label, tid: "fact-reporting" },
  ];
  const pillars = landing.pillars || [];
  const faqs = landing.faq_items || [];
  const canStart = data?.can_start;
  const blockReason = data?.block_reason;

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: COLORS.paper, color: COLORS.ink }} data-testid="survey-landing">
      {/* 01 Top navigation */}
      <header className="sticky top-0 z-20 border-b" style={{ borderColor: COLORS.line, background: "rgba(245,241,232,0.9)", backdropFilter: "blur(10px)" }} data-testid="landing-nav">
        <div className={`${WRAP} flex items-center justify-between py-4`}>
          <Logo showTagline />
          <nav className="hidden items-center gap-7 text-sm font-medium md:flex" aria-label="Navigasi utama">
            <a href="#studi" className="transition-colors hover:text-comiq-ink" style={{ color: COLORS.textSoft }}>Tentang studi</a>
            <a href="#partisipasi" className="transition-colors hover:text-comiq-ink" style={{ color: COLORS.textSoft }}>Partisipasi</a>
            <a href="#faq" className="transition-colors hover:text-comiq-ink" style={{ color: COLORS.textSoft }}>FAQ</a>
            <a href={`mailto:${email}`} className="btn-comiq btn-line !py-2 !text-xs" data-testid="nav-contact">Hubungi kami <ArrowUpRight size={13} /></a>
          </nav>
          <a href={`mailto:${email}`} className="btn-comiq btn-line !py-2 !text-xs md:hidden" data-testid="nav-contact-mobile">Hubungi kami</a>
        </div>
      </header>

      <main>
        {/* 02 Hero */}
        <section className={`${WRAP} grid items-center gap-12 py-16 md:py-24 lg:grid-cols-2`} data-testid="landing-hero">
          <div className="min-w-0">
            <span className="eyebrow" data-testid="landing-eyebrow">{landing.eyebrow}</span>
            <h1 className="font-serif-editorial mt-5 break-words text-[2.6rem] leading-[1.05] sm:text-6xl" data-testid="landing-headline">
              <Headline title={data?.study_title} emphasis={landing.headline_emphasis} />
            </h1>
            <p className="mt-6 max-w-xl break-words text-base leading-relaxed sm:text-lg" style={{ color: COLORS.textSoft }} data-testid="landing-subtitle">
              {landing.subtitle}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <a href="#studi" className="btn-comiq" style={{ background: COLORS.ink, color: COLORS.paper }} data-testid="cta-about">Tentang studi <ArrowDown size={16} /></a>
              <a href="#partisipasi" className="inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4" style={{ color: COLORS.ink }} data-testid="cta-participation">Informasi partisipasi <ArrowUpRight size={15} /></a>
            </div>
          </div>
          <div className="min-w-0 rounded-comiq-lg border p-6 sm:p-8" style={{ borderColor: COLORS.line, background: COLORS.surface }}>
            <HeroVisual />
          </div>
        </section>

        {/* 03 Facts row */}
        <div className={`${WRAP}`}>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-comiq border sm:grid-cols-4" style={{ borderColor: COLORS.line, background: COLORS.line }} data-testid="landing-facts">
            {facts.map((f) => (
              <div key={f.tid} className="px-5 py-6 sm:px-7" style={{ background: COLORS.surface }} data-testid={f.tid}>
                <div className="text-[0.68rem] font-semibold uppercase tracking-[0.16em]" style={{ color: COLORS.textSoft }}>{f.label}</div>
                <div className="mt-2 font-serif-editorial text-xl sm:text-2xl">{f.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 04 About the study */}
        <section id="studi" className={`${WRAP} scroll-mt-24 py-20 md:py-28`} data-testid="landing-about">
          <div className="grid gap-10 lg:grid-cols-2">
            <div className="min-w-0">
              <p className="eyebrow">Tentang studi</p>
              <h2 className="font-serif-editorial mt-4 break-words text-3xl sm:text-5xl" data-testid="about-headline">
                {landing.about_headline_1}<br /><em style={{ color: COLORS.orange }}>{landing.about_headline_2}</em>
              </h2>
            </div>
            <p className="min-w-0 self-end break-words text-base leading-relaxed sm:text-lg" style={{ color: COLORS.textSoft }} data-testid="about-body">
              {landing.about_body}
            </p>
          </div>

          {/* 05 Capability pillars */}
          <div className="mt-16 grid gap-5 md:grid-cols-3" data-testid="landing-pillars">
            {pillars.map((p, i) => (
              <article key={p.num} className="rounded-comiq border p-7 transition-transform duration-200 hover:-translate-y-1" style={{ borderColor: COLORS.line, background: COLORS.surface }} data-testid={`pillar-${i + 1}`}>
                <div className="flex items-start justify-between">
                  <span className="font-serif-editorial text-3xl" style={{ color: COLORS.orangeInk }}>{p.num}</span>
                  {PILLAR_ICONS[i % PILLAR_ICONS.length]}
                </div>
                <p className="mt-6 text-[0.68rem] font-semibold uppercase tracking-[0.16em]" style={{ color: COLORS.textSoft }}>{p.micro}</p>
                <h3 className="mt-2 text-lg font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: COLORS.textSoft }}>{p.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* 06 Participation banner */}
        <section id="partisipasi" className={`${WRAP} scroll-mt-24`} data-testid="landing-participation">
          <div className="flex flex-col items-start justify-between gap-8 rounded-comiq-lg border p-8 sm:p-12 lg:flex-row lg:items-center" style={{ borderColor: COLORS.line, background: "#EDE6D6" }}>
            <div className="max-w-xl">
              <p className="eyebrow">Akses melalui undangan</p>
              {data?.organization && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs" style={{ borderColor: COLORS.lineStrong, background: COLORS.surface, color: COLORS.ink }} data-testid="landing-org-chip">
                  <Building2 size={13} style={{ color: COLORS.orangeInk }} /> Diundang oleh <strong className="font-semibold">{data.organization}</strong>
                </div>
              )}
              {data?.is_demo && <div className="mt-3 inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="demo-badge">Data demo — bukan survei produksi</div>}
              <h2 className="font-serif-editorial mt-4 text-3xl sm:text-4xl" data-testid="participation-headline">{landing.participation_headline}</h2>
              <p className="mt-3 text-sm leading-relaxed sm:text-base" style={{ color: COLORS.textSoft }} data-testid="participation-body">{landing.participation_body}</p>
              {!canStart && blockReason && (
                <p className="mt-4 rounded-comiq-sm px-3 py-2 text-sm font-medium" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`landing-block-${blockReason}`}>
                  {BLOCK_MSG[blockReason] || "Tautan ini belum dapat digunakan saat ini."}
                </p>
              )}
            </div>
            <div className="shrink-0">
              {canStart ? (
                <button onClick={onStart} disabled={busy} className="btn-comiq btn-accent text-base disabled:opacity-50" data-testid="start-survey-btn">
                  {busy ? "Memuat…" : <>Mulai survey <ArrowUpRight size={17} /></>}
                </button>
              ) : (
                <a href={`mailto:${email}?subject=${encodeURIComponent("Bantuan akses studi ComIQ")}`} className="btn-comiq btn-accent" data-testid="landing-contact-btn">Hubungi tim riset <ArrowUpRight size={16} /></a>
              )}
            </div>
          </div>
        </section>

        {/* 07 FAQ */}
        <section id="faq" className={`${WRAP} scroll-mt-24 py-20 md:py-28`} data-testid="landing-faq">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
            <div className="min-w-0">
              <p className="eyebrow">Informasi peserta</p>
              <h2 className="font-serif-editorial mt-4 break-words text-3xl sm:text-5xl" data-testid="faq-headline">Sebelum<br /><em style={{ color: COLORS.orange }}>Anda mulai.</em></h2>
            </div>
            <div className="divide-y rounded-comiq border" style={{ borderColor: COLORS.line, background: COLORS.surface, "--tw-divide-opacity": 1, borderTop: "none" }}>
              {faqs.map((f, i) => (
                <div key={i} style={{ borderColor: COLORS.line }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-sm font-semibold transition-colors"
                    style={{ color: COLORS.ink }}
                    data-testid={`faq-item-${i}`}
                    aria-expanded={openFaq === i}
                  >
                    {f.q}
                    <ChevronDown size={17} className="shrink-0 transition-transform duration-200" style={{ color: COLORS.orangeInk, transform: openFaq === i ? "rotate(180deg)" : "none" }} />
                  </button>
                  {openFaq === i && (
                    <p className="px-6 pb-6 text-sm leading-relaxed" style={{ color: COLORS.textSoft }} data-testid={`faq-answer-${i}`}>{f.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* 08 Footer */}
      <footer className="border-t" style={{ borderColor: COLORS.line, background: COLORS.surface }} data-testid="landing-footer">
        <div className={`${WRAP} py-10`}>
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <Logo showTagline />
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <a href="https://www.comiq.id" className="transition-colors" style={{ color: COLORS.textSoft }}>Tentang ComIQ <ArrowUpRight size={13} className="inline" /></a>
              <a href={`mailto:${email}`} style={{ color: COLORS.textSoft }} data-testid="footer-contact">{email}</a>
            </div>
          </div>
          <div className="mt-8 flex flex-col items-start justify-between gap-3 border-t pt-6 text-xs sm:flex-row sm:items-center" style={{ borderColor: COLORS.line, color: COLORS.textSoft }}>
            <p>© 2026 ComIQ. Commercial Insight Quest.</p>
            <a href="/admin" className="font-semibold" style={{ color: COLORS.ink }} data-testid="footer-admin-link">Akses administrator <ArrowUpRight size={13} className="inline" /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}
